// The items of a workspace's settings and who sees them: "Personal" (the
// profile) for everyone, "Admin" items only for those who may use them.
import { useQuery } from "@tanstack/react-query";
import type {
  SpaceView,
  WorkspaceView,
} from "@trameria/cloud-api/workspace-shared";
import { m } from "@trameria/cloud-i18n";
import { iconOf, useApi } from "@trameria/ui-core";
import type { ComponentType, ReactNode } from "react";
import { useCan, useWorkspace, useWorkspaceHost } from "../context";
import { spacesQuery } from "../queries";
import type { WorkspaceSection } from "../sections";
import { Note, SettingsPage } from "./bits";

export type SettingsItemId =
  | "profile"
  | "general"
  | "members"
  | "roles"
  | "invites"
  | "features"
  | "spaces";

export interface WorkspaceSettingsItem {
  id: SettingsItemId;
  group: "personal" | "admin";
  /** Smaller comes first in its group (parts' items take a place by theirs). */
  order: number;
  label(): string;
  /** The item's mark in the sidebar (all there is of it when folded). */
  icon: ComponentType<{ className?: string }>;
  description(): string;
  /** Whether the person may use it (else it is neither listed nor shown). */
  allowed(can: (p: string) => boolean, spaces: readonly SpaceView[]): boolean;
}

/**
 * In the old web's order: what the workspace is (general, features, spaces),
 * then the people (members, roles), then emoji (a part's, at 60) and invites.
 */
export const SETTINGS_ITEMS: readonly WorkspaceSettingsItem[] = [
  {
    id: "profile",
    group: "personal",
    order: 10,
    label: () => m.ws_settings_profile(),
    icon: iconOf("person"),
    description: () => m.ws_settings_profile_description(),
    allowed: () => true,
  },
  {
    id: "general",
    group: "admin",
    order: 10,
    label: () => m.ws_settings_general(),
    icon: iconOf("sliders"),
    description: () => m.ws_settings_general_description(),
    allowed: (can) => can("workspace.manage"),
  },
  {
    id: "features",
    group: "admin",
    order: 20,
    label: () => m.ws_settings_features(),
    icon: iconOf("toggle"),
    description: () => m.ws_settings_features_description(),
    allowed: (can) => can("workspace.manage"),
  },
  {
    id: "spaces",
    group: "admin",
    order: 30,
    label: () => m.ws_settings_spaces(),
    icon: iconOf("space"),
    description: () => m.ws_settings_spaces_description(),
    // A space's managers may change their space without either permission.
    allowed: (can, spaces) =>
      can("space.manage") ||
      can("workspace.manage") ||
      spaces.some((s) => s.canManage),
  },
  {
    id: "members",
    group: "admin",
    order: 40,
    label: () => m.ws_settings_members(),
    icon: iconOf("people"),
    description: () => m.ws_settings_members_description(),
    allowed: (can) => can("member.manage") || can("role.manage"),
  },
  {
    id: "roles",
    group: "admin",
    order: 50,
    label: () => m.ws_settings_roles(),
    icon: iconOf("lock"),
    description: () => m.ws_settings_roles_description(),
    allowed: (can) => can("role.manage"),
  },
  {
    id: "invites",
    group: "admin",
    order: 70,
    label: () => m.ws_settings_invites(),
    icon: iconOf("invite"),
    description: () => m.ws_settings_invites_description(),
    allowed: (can) => can("workspace.invite"),
  },
];

export const settingsItem = (id: SettingsItemId) =>
  SETTINGS_ITEMS.find((i) => i.id === id) as WorkspaceSettingsItem;

/** An item as the sidebar lists it: the Workspace part's own, or a part's. */
export interface ListedSettingsItem {
  id: string;
  group: "personal" | "admin";
  label(): string;
  icon: ComponentType<{ className?: string }>;
  description(): string;
}

/** Where an item without an order goes: after this part's own. */
const AFTER_OWN = 1000;

/** Every item: this part's and the ones the sections add, by their order. */
function allItems(sections: readonly WorkspaceSection[]): Array<
  ListedSettingsItem & {
    allowed(can: (p: string) => boolean, spaces: readonly SpaceView[]): boolean;
  }
> {
  const added = sections.flatMap((s) => s.settings ?? []);
  return [
    ...SETTINGS_ITEMS,
    ...added.map((e) => ({
      ...e,
      order: e.order ?? AFTER_OWN,
      allowed: (can: (p: string) => boolean) => e.allowed(can),
    })),
  ].sort((a, b) => a.order - b.order);
}

/**
 * An admin item opened by someone who may not use it (a link from someone
 * else, or a permission just taken away): its name and why it shows nothing.
 */
export function Gate({ id, children }: { id: string; children: ReactNode }) {
  const { items } = useSettingsItems();
  const { sections } = useWorkspaceHost();
  const item = allItems(sections).find((i) => i.id === id);
  if (items.some((i) => i.id === id)) return children;
  return (
    <SettingsPage title={item?.label() ?? id}>
      <Note>{m.ws_settings_not_allowed()}</Note>
    </SettingsPage>
  );
}

/** The items this person may use here, in order. */
export function useSettingsItems(): {
  view: WorkspaceView;
  items: ListedSettingsItem[];
} {
  const { slug, view } = useWorkspace();
  const { sections } = useWorkspaceHost();
  const can = useCan();
  const spaces = useQuery(spacesQuery(useApi(), slug)).data ?? [];
  return {
    view,
    items: allItems(sections).filter((i) => i.allowed(can, spaces)),
  };
}
